import React, { useState, useEffect } from 'react';
import { useListState } from '@mantine/hooks';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NumberInputRow } from '@/components/form/NumberInputRow';
import { FeeAmountFieldset } from './FeeAmountFieldset';
import { useRushPremiumRuleInputs } from '../../user_configuration/user_invoice/invoice-inputs';
import { cn } from '@/lib/utils';
import { Check, X, Edit, Trash2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { EASE_EXPO_OUT, DURATION_FAST, DURATION_MEDIUM, DURATION_SLOW } from '@/lib/motion';

type Rule = { days: number | null; type: 'percentage' | 'fixed'; value: number | null };

interface Props {
  policy: Rule[];
  onChange: (rules: Rule[]) => void;
}

interface RushRuleItemProps {
  rule: Rule;
  index: number;
  policy: Rule[];
  onRemove: () => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onSave: (rule: Rule) => void;
  onCancel: () => void;
  onValidationChange?: (index: number, isValid: boolean) => void;
  isValid: boolean;
}

const RushRuleDisplay: React.FC<{
  rule: Rule;
  index: number;
  onEdit: () => void;
  onRemove: () => void;
}> = ({ rule, index, onEdit, onRemove }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10, scale: 0.95 }}
    transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
    className="flex items-center justify-between p-3 border rounded-lg bg-muted/10 hover:bg-muted/20 transition-colors cursor-pointer text-sm sm:text-base"
    onClick={onEdit}
  >
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <span className="font-medium">Tier {index + 1}:</span>
        <span>
          &lt; {rule.days} day{rule.days !== 1 ? 's' : ''} notice →
        </span>
        <span className="font-medium">
          {rule.type === 'percentage'
            ? `${rule.value}% premium`
            : `$${rule.value} premium`}
        </span>
      </div>
    </div>
    <div className="flex items-center gap-1">
      <Button
        size="icon-xs"
        variant="ghost"
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        aria-label="Edit tier"
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        <Edit className="h-3 w-3" />
      </Button>
      <Button
        size="icon-xs"
        variant="ghost"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        aria-label="Remove tier"
        className="text-muted-foreground hover:text-destructive transition-colors"
      >
        <Trash2 className="h-3 w-3" />
      </Button>
    </div>
  </motion.div>
);

const RushRuleItem = React.memo(React.forwardRef<HTMLDivElement, RushRuleItemProps>(({
  rule,
  index,
  policy,
  onRemove,
  isEditing,
  onStartEdit,
  onSave,
  onCancel,
  onValidationChange,
  isValid,
}, ref) => {
  const [localRule, setLocalRule] = useState<Rule>(rule);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    setLocalRule(rule);
  }, [rule]);

  const handleCancel = () => {
    setLocalRule(rule);
    setIsClosing(true);
    setTimeout(() => {
      onCancel();
      setIsClosing(false);
    }, 200);
  };

  const { daysInput, valuePercentageInput, valueFixedInput } = useRushPremiumRuleInputs(
    localRule,
    index,
    (updates) => {
      setLocalRule(prev => ({ ...prev, ...updates }));
      const newRule = { ...localRule, ...updates };
      const daysValid = newRule.days !== null && newRule.days >= 1;
      const valueValid = newRule.value !== null && (newRule.type === 'percentage'
        ? (newRule.value >= 0.01)
        : (newRule.value > 0));
      onValidationChange?.(index, daysValid && valueValid);
    }
  );

  if (!isEditing) {
    return (
      <RushRuleDisplay
        rule={rule}
        index={index}
        onEdit={onStartEdit}
        onRemove={onRemove}
      />
    );
  }

  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: isClosing ? 0 : 1, height: isClosing ? 0 : "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: isClosing ? DURATION_MEDIUM : DURATION_SLOW, ease: EASE_EXPO_OUT }}
      className="overflow-hidden"
    >
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: isClosing ? -20 : 0, opacity: isClosing ? 0 : 1 }}
        transition={{ duration: isClosing ? DURATION_FAST : DURATION_MEDIUM, delay: isClosing ? 0 : 0.1, ease: EASE_EXPO_OUT }}
        className="space-y-4 p-4 border rounded-lg bg-muted/20"
      >
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">
            {index === policy.length - 1 && rule.days === 1 && rule.value === 25 ? 'New Tier' : `Tier ${index + 1}`}
          </Label>
          <Button
            size="icon-xs"
            variant="ghost"
            onClick={onRemove}
            aria-label="Remove tier"
            className="text-muted-foreground hover:text-destructive transition-colors"
            data-skip-validation="true"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION_MEDIUM, delay: 0.2, ease: EASE_EXPO_OUT }}
        >
          <NumberInputRow
            id={`rush-days-${index}`}
            label="Lead Time Threshold"
            description="Bookings confirmed with fewer than this many days notice qualify"
            unit="days"
            unitPosition="right"
            inputProps={daysInput.inputProps}
            errorElement={daysInput.errorElement}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION_MEDIUM, delay: 0.3, ease: EASE_EXPO_OUT }}
        >
          <FeeAmountFieldset
            type={localRule.type}
            onTypeChange={(t) => {
              setLocalRule(prev => ({ ...prev, type: t, value: null }));
            }}
            percentageInputProps={valuePercentageInput.inputProps}
            percentageErrorElement={valuePercentageInput.errorElement}
            fixedInputProps={valueFixedInput.inputProps}
            fixedErrorElement={valueFixedInput.errorElement}
            idPrefix={`rush-${index}`}
            percentageLabel="Premium Percentage"
            fixedLabel="Premium Amount"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION_MEDIUM, delay: 0.4, ease: EASE_EXPO_OUT }}
          className="flex gap-2 justify-end"
        >
          <Button
            variant="outline"
            onClick={handleCancel}
            data-skip-validation="true"
            size="sm"
            className="transition-all hover:scale-105"
          >
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
          <Button
            onClick={() => onSave(localRule)}
            disabled={!isValid}
            className={cn(
              "transition-all hover:scale-105",
              !isValid && "opacity-50 cursor-not-allowed hover:scale-100"
            )}
            size="sm"
          >
            <Check className="w-4 h-4 mr-1" />
            Save
          </Button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}));

RushRuleItem.displayName = 'RushRuleItem';

export const RushPremiumPolicyConfig: React.FC<Props> = ({ policy, onChange }) => {
  const [ruleValidationStates, setRuleValidationStates] = useState<Record<number, boolean>>({});
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);
  const [workingPolicy, workingPolicyHandlers] = useListState<Rule>(policy);
  const [newlyAddedRuleIndex, setNewlyAddedRuleIndex] = useState<number | null>(null);

  useEffect(() => {
    const initialStates: Record<number, boolean> = {};
    policy.forEach((rule, index) => {
      const daysValid = rule.days !== null && rule.days >= 1;
      const valueValid = rule.value !== null && (rule.type === 'percentage'
        ? (rule.value >= 0.01)
        : (rule.value > 0));
      initialStates[index] = daysValid && valueValid;
    });
    setRuleValidationStates(initialStates);
    workingPolicyHandlers.setState(policy);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- workingPolicyHandlers is a Mantine useListState handler that changes identity each render; only sync when source policy changes
  }, [policy]);

  const handleValidationChange = (index: number, isValid: boolean) => {
    setRuleValidationStates(prev => ({ ...prev, [index]: isValid }));
  };

  const addRule = () => {
    const newRule: Rule = { days: 7, type: 'percentage' as const, value: 25 };
    const newIndex = workingPolicy.length;
    workingPolicyHandlers.append(newRule);
    setEditingRuleIndex(newIndex);
    setNewlyAddedRuleIndex(newIndex);

    const daysValid = newRule.days !== null && newRule.days >= 1;
    const valueValid = newRule.value !== null && (newRule.type === 'percentage'
      ? (newRule.value >= 0.01)
      : (newRule.value > 0));
    setRuleValidationStates(prev => ({ ...prev, [newIndex]: daysValid && valueValid }));
  };

  const removeRule = (idx: number) => {
    workingPolicyHandlers.remove(idx);
    const newPolicy = workingPolicy.filter((_, i) => i !== idx);
    onChange(newPolicy);

    if (editingRuleIndex === idx) {
      setEditingRuleIndex(null);
    } else if (editingRuleIndex !== null && editingRuleIndex > idx) {
      setEditingRuleIndex(editingRuleIndex - 1);
    }

    const newValidationStates: Record<number, boolean> = {};
    newPolicy.forEach((_, newIndex) => {
      const oldIndex = newIndex >= idx ? newIndex + 1 : newIndex;
      newValidationStates[newIndex] = ruleValidationStates[oldIndex] ?? false;
    });
    setRuleValidationStates(newValidationStates);
  };

  const startEditingRule = (index: number) => {
    setEditingRuleIndex(index);
    setNewlyAddedRuleIndex(null);
  };

  const saveRule = (index: number, rule: Rule) => {
    workingPolicyHandlers.setItem(index, rule);
    onChange(workingPolicy);
    setEditingRuleIndex(null);
    setNewlyAddedRuleIndex(null);
  };

  const cancelEditingRule = (index: number) => {
    const isCancellingNewRule = index === newlyAddedRuleIndex;

    if (isCancellingNewRule) {
      workingPolicyHandlers.remove(index);
      const newValidationStates = { ...ruleValidationStates };
      delete newValidationStates[index];
      setRuleValidationStates(newValidationStates);
    } else {
      workingPolicyHandlers.setState([...policy]);
    }

    setEditingRuleIndex(null);
    setNewlyAddedRuleIndex(null);
  };

  return (
    <>
      <div className="space-y-1">
        <Label className="text-base">Rush Premium Tiers</Label>
        <p className="text-xs text-muted-foreground">
          Charge a premium when a booking is confirmed with short lead time
        </p>
      </div>

      <div className="space-y-2">
        <AnimatePresence mode="popLayout">
          {workingPolicy.map((rule, idx) => (
            <RushRuleItem
              key={`rule-${idx}`}
              rule={rule}
              index={idx}
              policy={workingPolicy}
              onRemove={() => removeRule(idx)}
              isEditing={editingRuleIndex === idx}
              onStartEdit={() => startEditingRule(idx)}
              onSave={(rule) => saveRule(idx, rule)}
              onCancel={() => cancelEditingRule(idx)}
              onValidationChange={handleValidationChange}
              isValid={ruleValidationStates[idx] ?? false}
            />
          ))}
        </AnimatePresence>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
      >
        <Button
          variant="secondary"
          onClick={addRule}
          disabled={editingRuleIndex !== null}
          className={cn(
            "transition-all hover:scale-105",
            editingRuleIndex !== null && "opacity-50 cursor-not-allowed hover:scale-100"
          )}
        >
          + Add Tier
        </Button>
      </motion.div>
    </>
  );
};
